// src/components/MyEvents.js
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import memberstackDOM from "@memberstack/dom";
import { standardUserApi } from "../services/standardUserApi";
import { getMemberstackKey } from "../utils";
import useNoIndex from "../hooks/useNoIndex";
import { trackPageView, reportCheckoutSuccessConversion } from "../utils/tracking";
import Footer from "./Footer";
import InviteModal from "./InviteModal";
import MemberFaqs from "./MemberFaqs";
import FirstNamePrompt from "./FirstNamePrompt";
import LoadingState from "./LoadingState";

const currentKey = getMemberstackKey();
const memberstack = memberstackDOM.init({
  publicKey: currentKey,
});

/**
 * Subscriber-facing "My Events" page. Top section is the individual
 * Meetup-sourced events the member has RSVP'd to directly on OkGather;
 * bottom section is an automatically filtered view of the public /events
 * directory, scoped to just the event series they've signed up to before -
 * a quick way back in without re-browsing the full list.
 *
 * All data comes from GET /api/standard-user/my-events, which itself only
 * reads OkGather's own DB (the meetup_events cache + attendee table) - no
 * live Meetup API call happens on this page load.
 */
const MyEvents = () => {
  useNoIndex();

  const navigate = useNavigate();
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [signedUpSeries, setSignedUpSeries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [isVerifyingAuth, setIsVerifyingAuth] = useState(true);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [dropOutLoadingId, setDropOutLoadingId] = useState(null);
  // Invite Links: only shown to a currently-active (not trialing) paying
  // subscriber, stricter than the page's own hasPaidSubscription gate
  // below, which also allows trialing since that's enough to use the rest
  // of the page.
  const [canInvite, setCanInvite] = useState(false);
  const [inviteModal, setInviteModal] = useState({
    isOpen: false,
    themeId: null,
    themeName: "",
    gatheringDateLabel: "",
  });

  useEffect(() => {
    trackPageView("My Events");
  }, []);

  // Handle post-checkout purchase tracking + cleanup.
  // Checkout.js redirects here on success.
  useEffect(() => {
    reportCheckoutSuccessConversion('/my-events');
  }, []);

  useEffect(() => {
    const fetchMemberAndEvents = async () => {
      const token = localStorage.getItem("memberstackToken");

      if (!token) {
        setShowLoginModal(true);
        setIsVerifyingAuth(false);
        setIsLoading(false);
        return;
      }

      let memberResponse = null;
      try {
        memberResponse = await memberstack.getCurrentMember();
      } catch (err) {
        console.error("MyEvents › getCurrentMember error:", err);
      }

      if (!memberResponse || !memberResponse.data) {
        setShowLoginModal(true);
        setIsVerifyingAuth(false);
        setIsLoading(false);
        return;
      }

      const planConnections = memberResponse.data.planConnections || [];
      const hasPaidSubscription = planConnections.some((plan) => {
        const st = plan.status?.toLowerCase();
        const tp = plan.type?.toLowerCase();
        return tp === "subscription" && (st === "active" || st === "trialing");
      });
      if (!hasPaidSubscription) {
        navigate("/checkout");
        return;
      }
      setCanInvite(planConnections.some((plan) => {
        const st = plan.status?.toLowerCase();
        const tp = plan.type?.toLowerCase();
        return tp === "subscription" && st === "active";
      }));

      setIsVerifyingAuth(false);

      try {
        const data = await standardUserApi.getMyEvents(token);
        setUpcomingEvents(data.upcoming_events || []);
        setSignedUpSeries(data.signed_up_series || []);
      } catch (err) {
        console.error("Error fetching My Events:", err);
        setErrorMessage("Failed to load your events. You may need to login again (your login session may have expired)");
      } finally {
        setIsLoading(false);
      }
    };

    fetchMemberAndEvents();
  }, [navigate]);

  const handleDropOut = async (eventId) => {
    const token = localStorage.getItem("memberstackToken");
    if (!token) return;

    setDropOutLoadingId(eventId);
    try {
      await standardUserApi.dropOutOfMeetupEvent(token, eventId);
      setUpcomingEvents((prev) => prev.filter((e) => e.id !== eventId));
    } catch (err) {
      alert("Failed to drop out. Please try again.");
    } finally {
      setDropOutLoadingId(null);
    }
  };

  const formatEventDateTime = (isoString) => {
    if (!isoString) return "TBA";
    const dt = new Date(isoString);
    if (isNaN(dt.getTime())) return "TBA";
    return dt.toLocaleString("en-GB", {
      weekday: "short",
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatMeetupName = (urlname) => {
    if (!urlname) return null;
    return urlname
      .split("-")
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  if (isVerifyingAuth) {
    return <LoadingState />;
  }

  return (
    <div style={{ backgroundColor: "#FAFAFA", minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      {!showLoginModal && <FirstNamePrompt />}
      {showLoginModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0,0,0,0.6)",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "center",
            paddingTop: "80px",
            zIndex: 1200,
          }}
        >
          <div
            style={{
              background: "#fff",
              padding: "24px",
              borderRadius: "8px",
              textAlign: "center",
              maxWidth: "320px",
              width: "80%",
            }}
          >
            <p>Please login to view your events.</p>
            <button className="button-2 is-black w-button" onClick={() => navigate("/login")}>
              Continue
            </button>
          </div>
        </div>
      )}

      <section
        style={{
          padding: "50px 20px 30px",
          background: "linear-gradient(135deg, #FBFAFF, #F5F2FF)",
          borderBottom: "1px solid rgba(139, 95, 191, 0.1)",
          textAlign: "center",
        }}
      >
        <div style={{ maxWidth: "900px", margin: "0 auto" }}>
          <h1
            style={{
              fontSize: "clamp(1.75rem, 4.5vw, 2.5rem)",
              fontWeight: "800",
              marginBottom: "12px",
              background: "linear-gradient(135deg, #8B5FBF, #6B46C1)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              lineHeight: "1.2",
              paddingBottom: "0.1em",
            }}
          >
            My Events
          </h1>
          <p style={{ fontSize: "1.1rem", color: "#666", margin: 0 }}>
            Events you've RSVP'd to, and the event series you've joined before.
          </p>
        </div>
      </section>

      <section style={{ flex: 1, padding: "40px 20px", maxWidth: "1200px", margin: "0 auto", width: "100%" }}>
        {isLoading ? (
          <LoadingState label="Loading your events..." fullHeight={false} />
        ) : errorMessage ? (
          <p style={{ color: "red", textAlign: "center" }}>{errorMessage}</p>
        ) : (
          <>
            {/* ---------- Upcoming Events ---------- */}
            <div style={{ marginBottom: "60px" }}>
              <h2
                style={{
                  fontSize: "1.6rem",
                  fontWeight: "800",
                  color: "#111827",
                  marginBottom: "24px",
                }}
              >
                Your Upcoming Events
              </h2>

              {upcomingEvents.length === 0 ? (
                <div
                  style={{
                    textAlign: "center",
                    padding: "40px 24px",
                    backgroundColor: "#fff",
                    borderRadius: "14px",
                    border: "2px dashed #D1D5DB",
                  }}
                >
                  <div style={{ fontSize: "2rem", marginBottom: "10px" }}>📅</div>
                  <p style={{ fontSize: "1.05rem", color: "#374151", fontWeight: "700", margin: "0 0 6px" }}>
                    No upcoming events yet
                  </p>
                  <p style={{ fontSize: "0.95rem", color: "#6B7280", margin: "0 0 20px" }}>
                    Browse events and RSVP to see them here.
                  </p>
                  <button
                    onClick={() => navigate("/events")}
                    style={{
                      background: "linear-gradient(135deg, #8B5FBF, #6B46C1)",
                      color: "#fff",
                      border: "none",
                      borderRadius: "25px",
                      padding: "12px 28px",
                      fontSize: "0.95rem",
                      fontWeight: "700",
                      cursor: "pointer",
                    }}
                  >
                    Browse Events
                  </button>
                </div>
              ) : (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                    gap: "24px",
                  }}
                >
                  {upcomingEvents.map((event) => (
                    <div key={event.id} style={{ display: "flex", flexDirection: "column", height: "100%" }}>
                      <div
                        style={{
                          padding: "24px",
                          backgroundColor: "#fff",
                          borderRadius: "12px",
                          border: "1px solid rgba(139, 95, 191, 0.12)",
                          boxShadow: "0 4px 15px rgba(139, 95, 191, 0.08)",
                          flex: "1 1 auto",
                        }}
                      >
                        <h3 style={{ fontSize: "1.15rem", fontWeight: "700", color: "#333", marginBottom: "10px" }}>
                          {event.title}
                        </h3>
                        <p style={{ fontSize: "0.95rem", color: "#8B5FBF", fontWeight: "600", marginBottom: "6px" }}>
                          {formatEventDateTime(event.starts_at)}
                        </p>
                        {event.venue_name && (
                          <p style={{ fontSize: "0.9rem", color: "#666", marginBottom: "2px" }}>{event.venue_name}</p>
                        )}
                        {(event.venue_address || event.venue_city) && (
                          <p style={{ fontSize: "0.85rem", color: "#888", marginBottom: "6px" }}>
                            {[event.venue_address, event.venue_city].filter(Boolean).join(", ")}
                          </p>
                        )}
                        <a
                          href={event.event_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ fontSize: "0.85rem", color: "#8B5FBF", fontWeight: "600" }}
                        >
                          View on Meetup &rarr;
                        </a>
                      </div>
                      <div style={{ display: "flex", gap: "8px", marginTop: "10px" }}>
                        <button
                          onClick={() => handleDropOut(event.id)}
                          disabled={dropOutLoadingId === event.id}
                          style={{
                            flex: 1,
                            padding: "10px",
                            borderRadius: "8px",
                            border: "none",
                            backgroundColor: "#D1D5DB",
                            color: "#374151",
                            fontWeight: "600",
                            cursor: dropOutLoadingId === event.id ? "not-allowed" : "pointer",
                          }}
                        >
                          {dropOutLoadingId === event.id ? "Updating..." : "Drop out"}
                        </button>
                        {canInvite && event.theme_id && (
                          <button
                            onClick={() => setInviteModal({
                              isOpen: true,
                              themeId: event.theme_id,
                              themeName: event.theme_name || event.title,
                              gatheringDateLabel: formatEventDateTime(event.starts_at),
                            })}
                            style={{
                              flex: 1,
                              padding: "10px",
                              borderRadius: "8px",
                              border: "none",
                              backgroundColor: "#25D366",
                              color: "#fff",
                              fontWeight: "700",
                              cursor: "pointer",
                              boxShadow: "0 2px 8px rgba(37, 211, 102, 0.4)",
                            }}
                          >
                            🔗 Invite
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* ---------- Event Series You've Joined Before ---------- */}
            <div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "baseline",
                  flexWrap: "wrap",
                  gap: "10px",
                  marginBottom: "24px",
                }}
              >
                <h2 style={{ fontSize: "1.6rem", fontWeight: "800", color: "#111827", margin: 0 }}>
                  Event Series You've Joined Before
                </h2>
                <Link to="/events" style={{ color: "#8B5FBF", fontWeight: "600", textDecoration: "none", fontSize: "0.95rem" }}>
                  Browse all event series &rarr;
                </Link>
              </div>

              {signedUpSeries.length === 0 ? (
                <p style={{ color: "#6B7280", fontSize: "0.95rem" }}>
                  You haven't signed up to any event series yet - once you RSVP to an event, its series will show up here.
                </p>
              ) : (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
                    gap: "20px",
                  }}
                >
                  {signedUpSeries.map((entry) => (
                    <div
                      key={entry.url_slug}
                      style={{
                        padding: "24px",
                        backgroundColor: "white",
                        borderRadius: "16px",
                        border: "1px solid rgba(139, 95, 191, 0.12)",
                        boxShadow: "0 4px 15px rgba(139, 95, 191, 0.08)",
                      }}
                    >
                      <button
                        onClick={() => navigate(`/theme/${entry.url_slug}`)}
                        style={{
                          display: "block",
                          textAlign: "left",
                          width: "100%",
                          padding: 0,
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          fontFamily: "inherit",
                        }}
                      >
                        <h3 style={{ fontSize: "1.2rem", fontWeight: "700", color: "#333", margin: "0 0 8px", lineHeight: "1.3" }}>
                          {formatMeetupName(entry.meetup_urlname) || entry.theme_name}
                        </h3>
                        {entry.location_name && (
                          <span
                            style={{
                              display: "inline-block",
                              padding: "4px 12px",
                              borderRadius: "999px",
                              backgroundColor: "#6B46C1",
                              color: "#fff",
                              fontSize: "0.85rem",
                              fontWeight: "700",
                              marginBottom: "10px",
                            }}
                          >
                            📍 {entry.location_name}
                          </span>
                        )}
                        {(entry.meetup_event_title || entry.theme_name) && (
                          <p style={{ fontSize: "1rem", color: "#777", margin: 0 }}>
                            {entry.meetup_event_title || entry.theme_name}
                          </p>
                        )}
                      </button>
                      {canInvite && entry.theme_id && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setInviteModal({
                              isOpen: true,
                              themeId: entry.theme_id,
                              themeName: entry.theme_name || formatMeetupName(entry.meetup_urlname),
                              gatheringDateLabel: "",
                            });
                          }}
                          style={{
                            marginTop: "14px",
                            width: "100%",
                            padding: "10px",
                            borderRadius: "8px",
                            border: "none",
                            backgroundColor: "#25D366",
                            color: "#fff",
                            fontWeight: "700",
                            cursor: "pointer",
                            boxShadow: "0 2px 8px rgba(37, 211, 102, 0.4)",
                          }}
                        >
                          🔗 Invite
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </section>

      <InviteModal
        isOpen={inviteModal.isOpen}
        onClose={() => setInviteModal((prev) => ({ ...prev, isOpen: false }))}
        themeId={inviteModal.themeId}
        themeName={inviteModal.themeName}
        gatheringId={null}
        gatheringDateLabel={inviteModal.gatheringDateLabel}
      />

      <div style={{ maxWidth: "640px", margin: "0 auto", padding: "24px 20px 48px", width: "100%", boxSizing: "border-box" }}>
        <MemberFaqs />
      </div>

      <Footer />
    </div>
  );
};

export default MyEvents;
